import { useState, useCallback } from "react";
import { useCreate, useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { X, Save, Loader2, FileText, AlertTriangle } from "lucide-react";

interface DailyReportFormProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  contactId?: string | number;
}

export function DailyReportForm({ open, onClose, jobId, contactId }: DailyReportFormProps) {
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    report_date: today,
    summary: "",
    work_completed: "",
    issues: "",
    notes: "",
    weather: "",
    crew_count: 0,
    hours_worked: 0,
    delay_flag: false,
    delay_reason: "",
    delay_hours: 0,
  });

  const handleSave = useCallback(async () => {
    if (!form.summary.trim()) {
      notify("Summary is required", { type: "warning" });
      return;
    }
    setSaving(true);
    try {
      await create(
        "daily_reports",
        { data: { job_id: jobId, ...form } },
        { returnPromise: true }
      );

      await create("events", {
        data: {
          contact_id: contactId || null,
          job_id: jobId,
          event_type: form.delay_flag ? "delay_reported" : "daily_report_created",
          payload: {
            report_date: form.report_date,
            summary: form.summary.substring(0, 100),
            crew_count: form.crew_count,
            delay_flag: form.delay_flag,
            delay_hours: form.delay_hours,
          },
        },
      }, { returnPromise: true });

      notify("Daily report saved", { type: "success" });
      refresh();
      onClose();
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.includes("idx_daily_report_job_date")) {
        notify("A report already exists for this date", { type: "error" });
      } else {
        notify(`Failed: ${msg}`, { type: "error" });
      }
    } finally {
      setSaving(false);
    }
  }, [form, jobId, contactId, create, notify, refresh, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 overflow-y-auto py-8"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card className="w-full max-w-lg mx-4 shadow-xl">
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4" /> Daily Report
            </h3>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Date</label>
                <input type="date" value={form.report_date}
                  onChange={(e) => setForm((f) => ({ ...f, report_date: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Crew size</label>
                <input type="number" min={0} value={form.crew_count}
                  onChange={(e) => setForm((f) => ({ ...f, crew_count: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Hours worked</label>
                <input type="number" min={0} step={0.5} value={form.hours_worked}
                  onChange={(e) => setForm((f) => ({ ...f, hours_worked: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Weather</label>
              <input type="text" value={form.weather} placeholder="e.g. Sunny, 75°F"
                onChange={(e) => setForm((f) => ({ ...f, weather: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm" />
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Summary *</label>
              <textarea value={form.summary} rows={2}
                onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm resize-none" />
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Work completed</label>
              <textarea value={form.work_completed} rows={2}
                onChange={(e) => setForm((f) => ({ ...f, work_completed: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm resize-none" />
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Issues</label>
              <textarea value={form.issues} rows={2}
                onChange={(e) => setForm((f) => ({ ...f, issues: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm resize-none" />
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">Notes</label>
              <textarea value={form.notes} rows={2}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm resize-none" />
            </div>

            {/* Delay section */}
            <div className="border rounded p-3" style={{
              backgroundColor: form.delay_flag ? "#fef2f2" : "transparent",
              borderColor: form.delay_flag ? "#fecaca" : "#e5e7eb",
            }}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.delay_flag}
                  onChange={(e) => setForm((f) => ({ ...f, delay_flag: e.target.checked }))}
                  className="rounded" />
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-sm font-medium">Report delay</span>
              </label>
              {form.delay_flag && (
                <div className="mt-2 space-y-2">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Delay reason</label>
                    <input type="text" value={form.delay_reason}
                      onChange={(e) => setForm((f) => ({ ...f, delay_reason: e.target.value }))}
                      placeholder="e.g. Weather, material shortage"
                      className="w-full px-3 py-2 border rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Hours delayed</label>
                    <input type="number" min={0} step={0.5} value={form.delay_hours}
                      onChange={(e) => setForm((f) => ({ ...f, delay_hours: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-2 py-1.5 border rounded text-sm" style={{ maxWidth: 100 }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
            <button onClick={onClose} disabled={saving}
              className="px-3 py-1.5 rounded text-sm font-medium border text-gray-700 hover:bg-gray-100">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || !form.summary.trim()}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save Report
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
